import { useLayoutEffect, useRef, type ReactNode } from 'react';

/**
 * Primitivos visuais do novo layout do backoffice (Claude Design
 * "Prototipação do painel admin Vapt"). Seguem o design system
 * "Fogo Vivo" compartilhado com o painel do lojista.
 */

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'error' | 'warning' | 'info';

export function Badge({
  tone = 'neutral',
  dot = false,
  children,
  title,
}: {
  tone?: BadgeTone;
  dot?: boolean;
  children: ReactNode;
  title?: string;
}) {
  const toneClass = tone === 'neutral' ? '' : ` badge-${tone}`;
  return (
    <span className={`badge${toneClass}${dot ? ' badge-dot' : ''}`} title={title}>
      {children}
    </span>
  );
}

export function StatCard({
  label,
  value,
  delta,
  deltaTone = 'neutral',
  icon,
}: {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  deltaTone?: 'success' | 'error' | 'neutral';
  icon?: ReactNode;
}) {
  const deltaColor =
    deltaTone === 'success'
      ? 'var(--color-success)'
      : deltaTone === 'error'
        ? 'var(--color-error)'
        : 'var(--color-text-secondary)';
  return (
    // `group` com o rótulo como nome: o contador se acha pelo que ele conta, e não pela classe.
    <div
      className="card"
      role="group"
      aria-label={label}
      style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}
    >
      <div>
        <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>{label}</div>
        <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: 30, marginTop: 6, letterSpacing: '-0.02em' }}>
          {value}
        </div>
        {delta && (
          <div style={{ fontSize: 13, fontWeight: 500, color: deltaColor, marginTop: 6 }}>{delta}</div>
        )}
      </div>
      {icon && (
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-primary-light)',
            color: 'var(--color-primary-dark)',
            flex: '0 0 auto',
          }}
        >
          {icon}
        </span>
      )}
    </div>
  );
}

export function Avatar({
  name,
  size = 'sm',
  square = false,
}: {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  square?: boolean;
}) {
  const dims = { sm: 32, md: 40, lg: 56 } as const;
  const d = dims[size];
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return (
    <span
      aria-hidden="true"
      style={{
        width: d,
        height: d,
        borderRadius: square ? Math.round(d / 3) : '50%',
        flex: '0 0 auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-primary-light)',
        color: 'var(--color-primary-dark)',
        fontFamily: 'var(--font-heading)',
        fontWeight: 700,
        fontSize: d * 0.4,
      }}
    >
      {initials || '?'}
    </span>
  );
}

export function Tabs({
  tabs,
  value,
  onChange,
  ariaLabel,
}: {
  /** `label` pode trazer um selo ao lado do texto (ex.: "Fora da venda"). */
  tabs: Array<{ value: string; label: ReactNode }>;
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  // No celular a barra rola na horizontal (`.tabs` em global.css). A aba ativa pode estar fora da
  // faixa visível — `?aba=pacotes` abre na última —, então a barra rola até ela, sem mexer na
  // rolagem vertical da página (por isso `scrollLeft`, e não `scrollIntoView`).
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const active = list.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (!active) return;
    const start = active.offsetLeft - list.offsetLeft;
    const end = start + active.offsetWidth;
    if (start < list.scrollLeft) list.scrollLeft = start;
    else if (end > list.scrollLeft + list.clientWidth) list.scrollLeft = end - list.clientWidth;
  }, [value]);

  return (
    <div ref={listRef} className="tabs" role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              border: 'none',
              background: 'none',
              padding: '10px 14px',
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              fontSize: 15,
              color: active ? 'var(--color-primary-text)' : 'var(--color-text-secondary)',
              transition: 'color 0.15s ease',
            }}
          >
            {tab.label}
            <span
              style={{
                position: 'absolute',
                left: 8,
                right: 8,
                bottom: -1,
                height: 3,
                borderRadius: 3,
                background: active ? 'var(--color-primary)' : 'transparent',
                transition: 'background 0.15s ease',
              }}
            />
          </button>
        );
      })}
    </div>
  );
}
